import { useState } from "react";
import { Check, AlertCircle } from "lucide-react";
import { submitManuscript, GENRES } from "../lib/api";

const SUBMIT_IMG = "https://static.prod-images.emergentagent.com/jobs/85009444-7660-4f2b-b89f-6eee98ef2014/images/207be9b9ae1383a8da2abb83a57a970fa50a127caa74620a1b958e2bdc25568e.png";

const INITIAL_STATE = {
  full_name: "",
  email: "",
  phone: "",
  title: "",
  genre: "roman",
  synopsis: "",
  biography: "",
  pages_count: "",
  manuscript_excerpt: "",
};

export default function Soumission() {
  const [form, setForm] = useState(INITIAL_STATE);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: null, message: "" });
    try {
      const payload = {
        ...form,
        pages_count: form.pages_count ? parseInt(form.pages_count, 10) : null,
      };
      await submitManuscript(payload);
      setStatus({
        type: "success",
        message: "Votre soumission a bien été reçue. Nous l'examinerons avec attention et vous répondrons sous 6 à 8 semaines.",
      });
      setForm(INITIAL_STATE);
    } catch (err) {
      setStatus({
        type: "error",
        message: "Une erreur est survenue lors de l'envoi. Merci de vérifier les champs et réessayer.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-soumission" className="pt-24 min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        {/* Left visual */}
        <div className="lg:col-span-5 bg-[#F4F1EA] relative overflow-hidden hidden lg:block">
          <img
            src={SUBMIT_IMG}
            alt="Pages flottantes"
            className="w-full h-full object-cover sticky top-24"
            style={{ maxHeight: "calc(100vh - 6rem)" }}
          />
        </div>

        {/* Form */}
        <div className="lg:col-span-7 px-6 md:px-12 lg:px-20 py-16">
          <header className="mb-12 max-w-2xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body mb-6">
              Appel à manuscrits
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight font-light mb-6">
              Soumettre un <em className="text-[#2C402E]">manuscrit</em>
            </h1>
            <p className="text-base text-[#4A4A4A] font-body leading-relaxed">
              Vous êtes auteur·rice et souhaitez nous proposer votre œuvre ? Remplissez
              le formulaire ci-dessous. Tous les genres sont les bienvenus. Nous
              répondons sous 6 à 8 semaines.
            </p>
          </header>

          {status.type === "success" && (
            <div
              data-testid="submission-success"
              className="border border-[#2C402E] bg-[#FAF9F6] p-6 mb-8 flex gap-4"
            >
              <Check size={20} className="text-[#2C402E] flex-shrink-0 mt-0.5" />
              <p className="text-sm font-body text-[#1A1A1A]">{status.message}</p>
            </div>
          )}
          {status.type === "error" && (
            <div
              data-testid="submission-error"
              className="border border-[#9E4735] bg-[#FAF9F6] p-6 mb-8 flex gap-4"
            >
              <AlertCircle size={20} className="text-[#9E4735] flex-shrink-0 mt-0.5" />
              <p className="text-sm font-body text-[#1A1A1A]">{status.message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="submission-form" className="max-w-2xl space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Nom complet *" name="full_name" value={form.full_name} onChange={handleChange} required testid="input-full-name" />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={handleChange} required testid="input-email" />
            </div>
            <Field label="Téléphone" name="phone" value={form.phone} onChange={handleChange} testid="input-phone" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Titre du manuscrit *" name="title" value={form.title} onChange={handleChange} required testid="input-title" />
              <div>
                <label className="text-sm text-[#4A4A4A] font-body mb-2 block">Genre *</label>
                <select
                  name="genre"
                  value={form.genre}
                  onChange={handleChange}
                  data-testid="input-genre"
                  className="bg-transparent border-b border-[#D1CFC7] focus:border-[#2C402E] rounded-none px-0 py-3 w-full outline-none transition-colors font-body text-[#1A1A1A]"
                  required
                >
                  {GENRES.filter((g) => g.value !== "all").map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <Field label="Nombre de pages" name="pages_count" type="number" value={form.pages_count} onChange={handleChange} testid="input-pages" />

            <Textarea label="Synopsis (résumé de l'œuvre) *" name="synopsis" value={form.synopsis} onChange={handleChange} rows={5} required testid="input-synopsis" />
            <Textarea label="Biographie de l'auteur *" name="biography" value={form.biography} onChange={handleChange} rows={4} required testid="input-biography" />
            <Textarea label="Extrait du manuscrit (trois premiers chapitres)" name="manuscript_excerpt" value={form.manuscript_excerpt} onChange={handleChange} rows={8} testid="input-excerpt" />

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-manuscript-button"
              className="bg-[#2C402E] text-[#FAF9F6] hover:bg-[#1F2D21] disabled:bg-[#757575] px-12 py-4 font-body tracking-wide transition-all"
            >
              {submitting ? "Envoi en cours…" : "Envoyer le manuscrit"}
            </button>
            <p className="text-xs text-[#757575] font-body">
              En soumettant ce formulaire, vous acceptez que vos données soient
              utilisées pour le traitement de votre candidature.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="text-sm text-[#4A4A4A] font-body mb-2 block">{label}</label>
    <input
      {...props}
      data-testid={testid}
      className="bg-transparent border-b border-[#D1CFC7] focus:border-[#2C402E] rounded-none px-0 py-3 w-full outline-none transition-colors font-body text-[#1A1A1A]"
    />
  </div>
);

const Textarea = ({ label, testid, ...props }) => (
  <div>
    <label className="text-sm text-[#4A4A4A] font-body mb-2 block">{label}</label>
    <textarea
      {...props}
      data-testid={testid}
      className="bg-transparent border border-[#D1CFC7] focus:border-[#2C402E] rounded-none px-4 py-3 w-full outline-none transition-colors font-body text-[#1A1A1A] resize-none"
    />
  </div>
);
